import { LineStringFeature, extrasWithSurfaces } from '../../utils/models/LineStringFeature';
import { edgeValues, rangesForLine } from '../../utils/models/segmentSurfaces';
import { cumulativeDistancesMeters, splitInstructions } from '../../utils/models/turnInstructions';
import { generateID } from '../../utils/helpers/IDGenerator';
import { cumulativeDistances, sliceByDistance } from './utils/trailLine';

/**
 * A route's line string cut in two `offsetM` metres along it, for a day flag
 * dropped mid-leg: both halves keep the leg's routing profile, its surfaces
 * and its turns (divided at the cut, as a control point inserted there would
 * divide them), so splitting a plan into days never loses its directions.
 * Pure. Mirrors insertMarkerAtPoint, which cuts at a vertex rather than a
 * distance.
 */
export function cutLineStringAt(lineString: any, offsetM: number): [LineStringFeature, LineStringFeature] {
  const coords: number[][] = lineString?.lineStringGeometry?.coordinates ?? [];
  const cumDist = cumulativeDistances(coords);
  const legLength = cumDist[cumDist.length - 1] ?? 0;
  const at = Math.min(Math.max(offsetM, 0), legLength);

  const coords1 = sliceByDistance(coords, cumDist, 0, at);
  const coords2 = sliceByDistance(coords, cumDist, at, legLength);
  const first = LineStringFeature.coordsOnlyFeature(coords1);
  const second = LineStringFeature.coordsOnlyFeature(coords2);

  const props = lineString?.lineStringProperties;
  if (!props) return [first, second];

  const lengthOf = (c: number[][]) => cumulativeDistancesMeters(c)[c.length - 1] ?? 0;
  const [turns1, turns2] = splitInstructions(props.instructions ?? [], lengthOf(coords1), lengthOf(coords));
  // Per-edge surfaces: the cut edge is shared, so the first half keeps every
  // edge up to and including it and the second half every edge from it on.
  const surfaces = edgeValues(props.extras?.surface?.values, coords.length);
  const firstEdges = Math.max(coords1.length - 1, 0);
  const secondEdges = Math.max(coords2.length - 1, 0);
  first.lineStringProperties = {
    ...props,
    id: generateID(),
    instructions: turns1,
    extras: extrasWithSurfaces(props.extras, rangesForLine(coords1, surfaces.slice(0, firstEdges))),
  };
  second.lineStringProperties = {
    ...props,
    id: generateID(),
    instructions: turns2,
    extras: extrasWithSurfaces(props.extras, rangesForLine(coords2, surfaces.slice(Math.max(surfaces.length - secondEdges, 0)))),
  };
  return [first, second];
}
